import { useCallback, useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  Plus,
  RefreshCw,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";

import {
  api,
  type GeraetMitStatus,
  type PersonMitGeraeten,
  type PersonenUebersicht,
} from "@/api.ts";

const FARBEN = [
  "#1e40af", "#db2777", "#16a34a", "#d97706", "#7c3aed",
  "#0891b2", "#dc2626", "#4f46e5", "#059669", "#b45309",
];

function alterText(ms: number | null): string {
  if (ms === null) return "nie";
  const diff = Date.now() - ms;
  if (diff < 60_000) return "gerade eben";
  if (diff < 3_600_000) return `vor ${Math.round(diff / 60_000)} Min.`;
  if (diff < 86_400_000) return `vor ${Math.round(diff / 3_600_000)} Std.`;
  return `vor ${Math.round(diff / 86_400_000)} T.`;
}

function statusFarbe(status: GeraetMitStatus["status"]): string {
  return status === "bewegt" ? "#16a34a" : status === "steht" ? "#3b82f6" : "#94a3b8";
}

function statusText(g: GeraetMitStatus): string {
  if (g.status === "bewegt") return `bewegt (${lesbareEntfernung(g.bewegungM)})`;
  if (g.status === "steht") return "steht";
  return "veraltet";
}

function lesbareEntfernung(m: number): string {
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${Math.round(m)} m`;
}

export function PersonenSeite() {
  const [daten, setDaten] = useState<PersonenUebersicht | null>(null);
  const [lade, setLade] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const d = await api.personenUebersicht();
      setDaten(d);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLade(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  if (lade && !daten) {
    return (
      <div className="flex h-full items-center justify-center">
        <RefreshCw className="animate-spin text-text-muted" size={24} />
      </div>
    );
  }

  if (fehler && !daten) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <div>
          <h2 className="text-xl font-semibold text-danger">Fehler</h2>
          <p className="mt-2 text-text-muted">{fehler}</p>
        </div>
      </div>
    );
  }

  if (!daten) return null;

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-text">Personen &amp; Geräte</h1>
          <NeuePersonKnopf beiAnlegen={laden} vorhandeneFarben={daten.personen.map((p) => p.farbe)} />
        </div>

        <div className="space-y-4">
          {daten.personen.map((person) => (
            <PersonenKarte
              key={person.id}
              person={person}
              beiAenderung={laden}
            />
          ))}
        </div>

        {daten.ohnePerson.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-muted">
              Nicht zugeordnet ({daten.ohnePerson.length})
            </h2>
            <div className="space-y-1">
              {daten.ohnePerson.map((g) => (
                <GeraeteZeile
                  key={g.id}
                  geraet={g}
                  personen={daten.personen}
                  beiZuordnung={laden}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NeuePersonKnopf({
  beiAnlegen,
  vorhandeneFarben,
}: {
  beiAnlegen: () => void;
  vorhandeneFarben: string[];
}) {
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState("");
  const [farbe, setFarbe] = useState(() => FARBEN.find((f) => !vorhandeneFarben.includes(f)) ?? FARBEN[0]);
  const [speichere, setSpeichere] = useState(false);

  const anlegen = async () => {
    if (!name.trim()) return;
    setSpeichere(true);
    try {
      await api.personAnlegen(name.trim(), farbe);
      setName("");
      setOffen(false);
      beiAnlegen();
    } finally {
      setSpeichere(false);
    }
  };

  if (!offen) {
    return (
      <button
        onClick={() => setOffen(true)}
        className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm text-white hover:bg-primary-dark"
      >
        <UserPlus size={16} />
        Person anlegen
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-1">
        {FARBEN.map((f) => (
          <button
            key={f}
            onClick={() => setFarbe(f)}
            className={`size-6 rounded-full border-2 ${farbe === f ? "border-text" : "border-transparent"}`}
            style={{ backgroundColor: f }}
          />
        ))}
      </div>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && void anlegen()}
        placeholder="Name"
        className="w-36 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text"
      />
      <button
        onClick={() => void anlegen()}
        disabled={speichere || !name.trim()}
        className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary-dark disabled:opacity-50"
      >
        <Plus size={14} />
      </button>
      <button onClick={() => setOffen(false)} className="rounded p-1 hover:bg-page-bg">
        <X size={16} className="text-text-muted" />
      </button>
    </div>
  );
}

function PersonenKarte({
  person,
  beiAenderung,
}: {
  person: PersonMitGeraeten;
  beiAenderung: () => void;
}) {
  const [offen, setOffen] = useState(true);
  const [bearbeite, setBearbeite] = useState(false);
  const [name, setName] = useState(person.name);
  const [farbe, setFarbe] = useState(person.farbe);

  const speichern = async () => {
    if (!name.trim()) return;
    try {
      await api.personAktualisieren(person.id, {
        name: name.trim(),
        farbe,
      });
      setBearbeite(false);
      beiAenderung();
    } catch { /* */ }
  };

  const loeschen = async () => {
    try {
      await api.personLoeschen(person.id);
      beiAenderung();
    } catch { /* */ }
  };

  const bewegt = person.geraete.filter((g) => g.status === "bewegt");

  return (
    <div className="rounded-xl border border-border bg-surface">
      <div className="flex items-center gap-3 p-4">
        <button
          onClick={() => setOffen(!offen)}
          className="rounded p-0.5 hover:bg-page-bg"
        >
          {offen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        <div
          className="size-8 rounded-full"
          style={{ backgroundColor: person.farbe }}
        />

        {bearbeite ? (
          <div className="flex flex-1 items-center gap-2">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void speichern()}
              className="w-36 rounded border border-border bg-surface px-2 py-1 text-sm text-text"
            />
            <div className="flex gap-1">
              {FARBEN.map((f) => (
                <button
                  key={f}
                  onClick={() => setFarbe(f)}
                  className={`size-5 rounded-full border-2 ${farbe === f ? "border-text" : "border-transparent"}`}
                  style={{ backgroundColor: f }}
                />
              ))}
            </div>
            <button
              onClick={() => void speichern()}
              className="rounded bg-primary px-2 py-1 text-xs text-white"
            >
              OK
            </button>
            <button
              onClick={() => { setBearbeite(false); setName(person.name); setFarbe(person.farbe); }}
              className="rounded p-1 hover:bg-page-bg"
            >
              <X size={14} className="text-text-muted" />
            </button>
          </div>
        ) : (
          <div className="flex flex-1 items-center gap-2">
            <button
              onClick={() => setBearbeite(true)}
              className="text-base font-semibold text-text hover:underline"
            >
              {person.name}
            </button>
            <span className="text-xs text-text-muted">
              {person.geraete.length} Gerät{person.geraete.length !== 1 ? "e" : ""}
              {bewegt.length > 0 && (
                <span className="ml-1 text-success">{bewegt.length} bewegt</span>
              )}
            </span>
          </div>
        )}

        <button
          onClick={() => void loeschen()}
          className="rounded p-1.5 text-text-muted hover:bg-red-50 hover:text-danger"
          title="Person löschen"
        >
          <Trash2 size={15} />
        </button>
      </div>

      {offen && (
        <div className="border-t border-border px-4 pb-3 pt-1">
          {person.geraete.length === 0 ? (
            <p className="py-3 text-center text-sm text-text-muted">
              Noch keine Geräte zugeordnet
            </p>
          ) : (
            <div className="space-y-0.5">
              {person.geraete.map((g) => (
                <GeraeteZeile
                  key={g.id}
                  geraet={g}
                  personen={[]}
                  beiZuordnung={beiAenderung}
                  abloesbar
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GeraeteZeile({
  geraet,
  personen,
  beiZuordnung,
  abloesbar,
}: {
  geraet: GeraetMitStatus;
  personen: PersonMitGeraeten[];
  beiZuordnung: () => void;
  abloesbar?: boolean;
}) {
  const [zielOffen, setZielOffen] = useState(false);

  const zuordnen = async (personId: number | null) => {
    try {
      await api.quelleZuordnen(geraet.id, personId);
      setZielOffen(false);
      beiZuordnung();
    } catch { /* */ }
  };

  return (
    <div className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-page-bg">
      <GripVertical size={14} className="shrink-0 text-text-muted/40" />

      <span
        className="size-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: statusFarbe(geraet.status) }}
        title={statusText(geraet)}
      />

      <span className="min-w-0 flex-1 truncate text-sm text-text">{geraet.name}</span>

      <span className="shrink-0 text-xs text-text-muted">
        {statusText(geraet)}
      </span>

      <span className="shrink-0 text-xs text-text-muted">
        {alterText(geraet.zuletztMs)}
      </span>

      {abloesbar && (
        <button
          onClick={() => void zuordnen(null)}
          className="rounded p-1 text-text-muted hover:text-danger"
          title="Zuordnung lösen"
        >
          <X size={13} />
        </button>
      )}

      {!abloesbar && personen.length > 0 && (
        <div className="relative">
          <button
            onClick={() => setZielOffen(!zielOffen)}
            className="flex items-center gap-1 rounded border border-border px-2 py-0.5 text-xs text-text-muted hover:bg-surface"
          >
            Zuordnen
            <ChevronDown size={12} />
          </button>
          {zielOffen && (
            <div className="absolute right-0 top-full z-10 mt-1 w-40 rounded-lg border border-border bg-surface py-1 shadow-lg">
              {personen.map((p) => (
                <button
                  key={p.id}
                  onClick={() => void zuordnen(p.id)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-page-bg"
                >
                  <span
                    className="size-3 shrink-0 rounded-full"
                    style={{ backgroundColor: p.farbe }}
                  />
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
